import { useState, useEffect, useMemo } from 'react';
import { Archive, Search, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { dataAntiguaApi } from '../lib/api';

interface Columna {
  key: string;
  label: string;
  tipo?: 'fecha' | 'moneda' | 'largo';
}

interface Pestaña {
  key: string;
  label: string;
}

interface Grupo {
  titulo: string;
  nota: string;
  pestañas: Pestaña[];
}

// Solo las vistas que tienen datos. Las tablas antiguas vacías (adelantos generales,
// pagos de crédito y de mantenimiento) se quitaron para no mostrar pestañas en blanco.
const GRUPOS: Grupo[] = [
  {
    titulo: 'Antes de agosto 2026',
    nota: 'Lo registrado antes de que el sistema arrancara de cero en agosto 2026.',
    pestañas: [
      { key: 'mantenimientos', label: 'Mantenimientos' },
      { key: 'ventas', label: 'Ventas' },
      { key: 'caja_chica', label: 'Caja (ingresos y gastos)' },
      { key: 'adelantos_mecanicos', label: 'Adelantos a mecánicos' },
      { key: 'pagos_buses', label: 'Pagos de buses' },
    ],
  },
  {
    titulo: 'Deudas al 06/10/2026',
    nota: 'Deudas que había al 06/10/2026, puestas en cero por decisión de gerencia (ingreso de personal nuevo).',
    pestañas: [
      { key: 'deudas_resumen', label: 'Resumen por propietario' },
      { key: 'deudas_buses', label: 'Deuda por bus' },
      { key: 'deudas_ventas', label: 'Ventas y productos' },
      { key: 'deudas_antiguas', label: 'Deuda antigua' },
    ],
  },
];

const TODAS = GRUPOS.flatMap((g) => g.pestañas.map((p) => ({ ...p, grupo: g })));
const POR_PAGINA = 100;

function fechaTexto(v: any) {
  if (!v) return '';
  const m = /^(\d+)-(\d{2})-(\d{2})$/.exec(String(v));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
}

function soles(v: any) {
  if (v === null || v === undefined || v === '') return '';
  const n = parseFloat(String(v));
  return isNaN(n) ? '' : `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function textoCelda(col: Columna, v: any) {
  if (col.tipo === 'fecha') return fechaTexto(v);
  if (col.tipo === 'moneda') return soles(v);
  return v === null || v === undefined ? '' : String(v);
}

const DataAntigua = () => {
  const [activa, setActiva] = useState('mantenimientos');
  const [columnas, setColumnas] = useState<Columna[]>([]);
  const [filas, setFilas] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [pagina, setPagina] = useState(1);

  const pestaña = TODAS.find((p) => p.key === activa)!;

  useEffect(() => {
    cargar(activa);
    setBusqueda(''); setDesde(''); setHasta(''); setPagina(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa]);

  async function cargar(vista: string) {
    setCargando(true);
    try {
      const data = await dataAntiguaApi.legible(vista);
      setColumnas(data.columnas || []);
      setFilas(data.filas || []);
    } catch (error) {
      console.error('Error cargando data antigua:', error);
      toast.error('No se pudo cargar la data antigua');
      setColumnas([]); setFilas([]);
    } finally {
      setCargando(false);
    }
  }

  const colFecha = columnas.find((c) => c.tipo === 'fecha')?.key;
  const colsMonto = columnas.filter((c) => c.tipo === 'moneda');

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas.filter((f) => {
      if (colFecha && (desde || hasta)) {
        const d = String(f[colFecha] || '');
        if (desde && d < desde) return false;
        if (hasta && d > hasta) return false;
      }
      if (!q) return true;
      return columnas.some((c) => textoCelda(c, f[c.key]).toLowerCase().includes(q));
    });
  }, [filas, columnas, busqueda, desde, hasta, colFecha]);

  const totales = useMemo(() => {
    const t: Record<string, number> = {};
    colsMonto.forEach((c) => { t[c.key] = filtradas.reduce((s, f) => s + (parseFloat(String(f[c.key] ?? 0)) || 0), 0); });
    return t;
  }, [filtradas, colsMonto]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const visibles = filtradas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  function nombreArchivo(ext: string) {
    const base = `Data_Antigua_${pestaña.grupo.titulo}_${pestaña.label}`.replace(/[^\wáéíóúñÁÉÍÓÚÑ]+/g, '_');
    return `${base}.${ext}`;
  }

  function filtroTexto() {
    const partes = [];
    if (desde || hasta) partes.push(`Fechas: ${desde ? fechaTexto(desde) : 'inicio'} a ${hasta ? fechaTexto(hasta) : 'hoy'}`);
    if (busqueda.trim()) partes.push(`Búsqueda: "${busqueda.trim()}"`);
    return partes.join(' · ');
  }

  function descargarExcel() {
    const encabezado = columnas.map((c) => c.label);
    const datos = filtradas.map((f) => columnas.map((c) => {
      const v = f[c.key];
      if (c.tipo === 'moneda') return v === null || v === undefined ? null : parseFloat(String(v));
      if (c.tipo === 'fecha') return fechaTexto(v);
      return v ?? '';
    }));
    const totalFila = columnas.map((c, i) => (c.tipo === 'moneda' ? Math.round(totales[c.key] * 100) / 100 : i === 0 ? 'TOTAL' : ''));
    const aoa: any[][] = [
      [`${pestaña.grupo.titulo} — ${pestaña.label}`],
      [filtroTexto() || pestaña.grupo.nota],
      [],
      encabezado,
      ...datos,
      ...(colsMonto.length ? [totalFila] : []),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = columnas.map((c) => ({ wch: c.tipo === 'largo' ? 60 : c.tipo === 'moneda' ? 14 : c.tipo === 'fecha' ? 12 : 24 }));
    // formato de soles en las columnas de monto
    columnas.forEach((c, ci) => {
      if (c.tipo !== 'moneda') return;
      for (let r = 4; r < aoa.length; r++) {
        const ref = XLSX.utils.encode_cell({ r, c: ci });
        if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = '"S/" #,##0.00';
      }
    });
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 3, c: 0 }, e: { r: 3 + datos.length, c: columnas.length - 1 } }) };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, pestaña.label.slice(0, 31));
    XLSX.writeFile(wb, nombreArchivo('xlsx'));
  }

  function descargarPDF() {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(14);
    doc.text(`Sistema Rápido — Data Antigua`, 14, 14);
    doc.setFontSize(11);
    doc.text(`${pestaña.grupo.titulo} · ${pestaña.label}`, 14, 21);
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(`${filtroTexto() || pestaña.grupo.nota}  ·  ${filtradas.length} registro(s)  ·  generado ${new Date().toLocaleDateString('es-PE')}`, 14, 26);
    doc.setTextColor(0);
    const pie = colsMonto.length
      ? [columnas.map((c, i) => (c.tipo === 'moneda' ? soles(totales[c.key]) : i === 0 ? 'TOTAL' : ''))]
      : undefined;
    autoTable(doc, {
      startY: 30,
      head: [columnas.map((c) => c.label)],
      body: filtradas.map((f) => columnas.map((c) => textoCelda(c, f[c.key]))),
      foot: pie,
      showFoot: 'lastPage',
      styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak', valign: 'top' },
      headStyles: { fillColor: [31, 78, 120], textColor: 255 },
      footStyles: { fillColor: [221, 235, 247], textColor: 0, fontStyle: 'bold' },
      columnStyles: Object.fromEntries(columnas.map((c, i) => [i,
        c.tipo === 'moneda' ? { halign: 'right', cellWidth: 22 }
          : c.tipo === 'fecha' ? { cellWidth: 18 }
          : c.tipo === 'largo' ? { cellWidth: 'auto' } : {}])),
      didDrawPage: () => {
        const n = doc.getNumberOfPages();
        doc.setFontSize(7);
        doc.text(`Página ${n}`, doc.internal.pageSize.getWidth() - 25, doc.internal.pageSize.getHeight() - 6);
      },
    });
    doc.save(nombreArchivo('pdf'));
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-2 mb-1">
        <Archive className="w-6 h-6 text-gray-500" />
        <h1 className="text-2xl font-bold text-gray-800">Data Antigua</h1>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Consulta de referencia. Solo lectura: no se puede editar ni afecta ningún saldo o reporte activo.
      </p>

      {GRUPOS.map((g) => (
        <div key={g.titulo} className="mb-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{g.titulo}</div>
          <div className="flex flex-wrap gap-1">
            {g.pestañas.map((p) => (
              <button
                key={p.key}
                onClick={() => setActiva(p.key)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${
                  activa === p.key
                    ? 'bg-blue-600 border-blue-600 text-white font-semibold'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      <p className="text-xs text-gray-500 mt-3 mb-3">{pestaña.grupo.nota}</p>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
          <input
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
            placeholder="Buscar (bus, nombre, producto, detalle...)"
            className="w-full border rounded-lg pl-8 pr-3 py-2 text-sm"
          />
        </div>
        {colFecha && (
          <>
            <label className="text-xs text-gray-500">Desde
              <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPagina(1); }} className="block border rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs text-gray-500">Hasta
              <input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPagina(1); }} className="block border rounded-lg px-2 py-1.5 text-sm" />
            </label>
          </>
        )}
        <button onClick={descargarExcel} disabled={!filtradas.length}
          className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-40">
          <FileSpreadsheet className="w-4 h-4" /> Excel
        </button>
        <button onClick={descargarPDF} disabled={!filtradas.length}
          className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
          <FileText className="w-4 h-4" /> PDF
        </button>
      </div>

      {cargando ? (
        <p className="text-gray-500">Cargando...</p>
      ) : filtradas.length === 0 ? (
        <p className="text-gray-500">{filas.length ? 'Ningún registro coincide con el filtro.' : 'No hay datos en esta categoría.'}</p>
      ) : (
        <>
          <div className="flex flex-wrap justify-between items-center text-xs text-gray-500 mb-1 gap-2">
            <span>{filtradas.length} registro(s){filtradas.length !== filas.length ? ` de ${filas.length}` : ''}</span>
            {colsMonto.length > 0 && (
              <span className="font-semibold text-gray-700">
                {colsMonto.map((c) => `${c.label}: ${soles(totales[c.key])}`).join(' · ')}
              </span>
            )}
          </div>
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {columnas.map((c) => (
                    <th key={c.key} className={`px-3 py-2 font-medium text-gray-600 whitespace-nowrap ${c.tipo === 'moneda' ? 'text-right' : 'text-left'}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibles.map((fila, i) => (
                  <tr key={i} className="align-top hover:bg-gray-50">
                    {columnas.map((c) => (
                      <td key={c.key} className={`px-3 py-2 text-gray-700 ${
                        c.tipo === 'moneda' ? 'text-right font-mono whitespace-nowrap'
                          : c.tipo === 'largo' ? 'min-w-[260px] max-w-[480px] whitespace-pre-line'
                          : 'whitespace-nowrap'}`}>
                        {textoCelda(c, fila[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPaginas > 1 && (
            <div className="flex justify-center items-center gap-3 mt-3 text-sm">
              <button disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Anterior</button>
              <span className="text-gray-500">Página {pagina} de {totalPaginas}</span>
              <button disabled={pagina >= totalPaginas} onClick={() => setPagina(pagina + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataAntigua;
